import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Shield,
  CheckCircle,
  XCircle,
  CreditCard,
  BookOpen,
  Award,
  Lock,
  Unlock
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001/api";

const UserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  // Try context first, fallback to localStorage for new tabs
  const token = authContext?.token || localStorage.getItem("token");

  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  // Debug logging
  useEffect(() => {
    console.log("=== UserDetailPage Mounted ===");
    console.log("userId:", userId);
    console.log("token from context:", authContext?.token);
    console.log("token from localStorage:", localStorage.getItem("token"));
    console.log("final token:", token);
  }, []);

  useEffect(() => {
    if (userId && token) {
      fetchUserDetails();
      fetchUserSubscription();
    } else if (userId && !token) {
      console.log("Waiting for token...");
      setLoading(false);
    }
  }, [userId, token]);

  const fetchUserDetails = async () => {
    if (!token || !userId) {
      console.error("Missing token or userId");
      return;
    }

    try {
      console.log("=== Fetching User Details ===");
      console.log("userId:", userId);
      console.log("API URL:", `${API}/admin/users/${userId}`);
      console.log("Token:", token?.substring(0, 20) + "...");
      
      const response = await axios.get(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log("✓ User data received successfully");
      console.log("User:", response.data.name, response.data.email);
      setUser(response.data);
    } catch (error) {
      console.error("=== Error Fetching User ===");
      console.error("Error:", error.message);
      console.error("Response data:", error.response?.data);
      console.error("Status code:", error.response?.status);
      
      if (error.response?.status === 404) {
        toast.error("User not found - Invalid user ID");
        setTimeout(() => navigate("/admin"), 2000);
      } else if (error.response?.status === 401) {
        toast.error("Unauthorized - Please login again");
        setTimeout(() => navigate("/login"), 2000);
      } else {
        toast.error(`Failed to fetch user: ${error.response?.data?.detail || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUserSubscription = async () => {
    if (!token || !userId) return;

    try {
      const response = await axios.get(`${API}/admin/user-subscriptions?skip=0&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userSub = response.data.subscriptions?.find(sub => sub.user_id === userId && sub.status === "active");
      setSubscription(userSub);
      console.log("Subscription found:", userSub ? userSub.plan_name : "None");
    } catch (error) {
      console.error("Error fetching subscription:", error);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleVerifyUser = async () => {
    try {
      await axios.post(`${API}/admin/users/${userId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User verified successfully");
      fetchUserDetails();
    } catch (error) {
      console.error("Error verifying user:", error);
      toast.error("Failed to verify user");
    }
  };

  const handleLockUser = async () => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/lock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User locked successfully");
      fetchUserDetails();
    } catch (error) {
      console.error("Error locking user:", error);
      toast.error("Failed to lock user");
    }
  };

  const handleUnlockUser = async () => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/unlock`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User unlocked successfully");
      fetchUserDetails();
    } catch (error) {
      console.error("Error unlocking user:", error);
      toast.error("Failed to unlock user");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h2 className="text-2xl font-bold mb-4">User Not Found</h2>
        <Button onClick={handleBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={handleBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold">User Details</h1>
              <p className="text-gray-600">View and manage user information</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!user.is_verified && (
              <Button onClick={handleVerifyUser}>
                <CheckCircle className="w-4 h-4 mr-2" />
                Verify User
              </Button>
            )}
            {user.is_locked ? (
              <Button variant="outline" onClick={handleUnlockUser}>
                <Unlock className="w-4 h-4 mr-2" />
                Unlock
              </Button>
            ) : (
              <Button variant="outline" onClick={handleLockUser}>
                <Lock className="w-4 h-4 mr-2" />
                Lock
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - User Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Full Name</label>
                    <p className="text-lg font-semibold">{user.name || "N/A"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Role</label>
                    <div className="mt-1">
                      <Badge variant={user.role === "admin" ? "default" : "secondary"}>
                        {user.role || "user"}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p className="text-base flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      {user.email}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Mobile</label>
                    <p className="text-base flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      {user.mobile || "Not provided"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Grade/Education</label>
                    <p className="text-base flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-gray-400" />
                      {user.grade || "Not specified"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Location</label>
                    <p className="text-base flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      {user.city || user.state || "Not provided"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Account Status */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Account Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-full ${user.is_verified ? 'bg-green-100' : 'bg-yellow-100'}`}>
                      {user.is_verified ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-yellow-600" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email Status</p>
                      <p className="font-medium">
                        {user.is_verified ? "Verified" : "Not Verified"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-full ${user.is_locked ? 'bg-red-100' : 'bg-green-100'}`}>
                      {user.is_locked ? (
                        <Lock className="w-5 h-5 text-red-600" />
                      ) : (
                        <Unlock className="w-5 h-5 text-green-600" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Account Status</p>
                      <p className="font-medium">
                        {user.is_locked ? "Locked" : "Active"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="p-2 rounded-full bg-blue-100">
                      <Calendar className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Joined</p>
                      <p className="font-medium">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Additional Details */}
            {user.interests && user.interests.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Interests & Goals</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {user.interests && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Interests</label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {user.interests.map((interest, idx) => (
                            <Badge key={idx} variant="outline">{interest}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {user.career_goal && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Career Goal</label>
                        <p className="text-base mt-1">{user.career_goal}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Subscription & Quick Actions */}
          <div className="space-y-6">
            {/* Subscription Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Subscription
                </CardTitle>
              </CardHeader>
              <CardContent>
                {subscription ? (
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Plan</label>
                      <p className="text-lg font-semibold">{subscription.plan_name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Status</label>
                      <div className="mt-1">
                        <Badge variant={subscription.status === "active" ? "default" : "secondary"}>
                          {subscription.status}
                        </Badge>
                      </div>
                    </div>
                    <Separator />
                    <div>
                      <label className="text-sm font-medium text-gray-500">Valid Until</label>
                      <p className="text-base">
                        {new Date(subscription.end_date).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Days Remaining</label>
                      <p className="text-base font-medium text-blue-600">
                        {Math.max(0, Math.ceil((new Date(subscription.end_date) - new Date()) / (1000 * 60 * 60 * 24)))} days
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Award className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">No active subscription</p>
                    <Badge variant="secondary" className="mt-2">Free Tier</Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Info */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">User ID</label>
                  <p className="text-xs font-mono bg-gray-100 p-2 rounded mt-1 break-all">
                    {user.id}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Language</label>
                  <p className="text-base mt-1">{user.language || "English"}</p>
                </div>
                {user.subscription_tier && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Subscription Tier</label>
                    <p className="text-base mt-1">{user.subscription_tier}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDetailPage;
